import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Prompt, useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { ErrorIcon, SaveIcon } from '@sharedCR/coreDesignSystem/icons';
import { checkIsInternalCompany } from 'app/auth/authUtils';
import { FORMS_ROUTING_PARAMS, FormsPaths } from 'app/configs/routesConfig/forms';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import FormEditor from 'app/shared/components/forms/FormEditor';
import useIsPrismFormEditorEnabled from 'app/shared/components/forms/FormEditor/hooks/useIsPrismFormEditorEnabled';
import withFormTemplateContent, { WithFormContentPropTypes } from 'hocs/withFormTemplateContent';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { ConfirmationModalType, UNSAVED_LEAVE_PROMPT_MESSAGE } from 'constants/confirmationDialog';
import { DynamicFieldType } from 'constants/dynamicFields';
import { FeatureFlag } from 'constants/featureFlags';
import { FormType, FormViewerNavigationType } from 'constants/forms';
import { getStateName } from 'constants/states';
import useBeforeUnloadPrompt from 'hooks/useBeforeUnloadPrompt';
import useDebounce from 'hooks/useDebounce';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useFormTemplateActions from 'hooks/useFormTemplateActions';
import { getIsFormNonOverridable, getPrismEditorBlockReason } from 'utils/forms';

import { getIsFromInternalCompany } from 'store/auth/selectors';
import { getDynamicFieldsSchemaAction } from 'store/forms/actions';
import {
  selectDynamicFieldsNamesById,
  selectDynamicFieldsSchema,
  selectFormNavigationType,
} from 'store/forms/selectors';
import { openConfirmationDialogAction } from 'store/global/actions';
import { selectDocumentTypeNameById } from 'store/system/selectors';

import FormChangeHistoryButton from '../FormChangeHistory/FormChangeHistoryButton';
import { FormsTestIds } from '../Forms.testIds';

import { OWN_VIEW_REDIRECT_HISTORY_ACTION, SCROLL_DELAY_MS } from './FormBuilder.constants';
import {
  getIsFormContentUnchanged,
  replaceFontFamily,
  setTableStylesToContent,
} from './FormBuilder.utils';
import usePrismFormExport from './hooks/usePrismFormExport';

const FormBuilder = ({ form, isLoading, setIsLoading, formParams }) => {
  const dispatch = useDispatch();

  const navigationType = useSelector(selectFormNavigationType);
  const pathParams = useParams();
  const { selectedPage, formId } =
    navigationType === FormViewerNavigationType.STATE ? formParams : pathParams;

  const {
    state,
    template,
    name,
    formType,
    documentTypeId,
    withPageNumbers,
    isTemplateSavedInPrism,
    isSeededSystemForm,
    isGlobal,
  } = form;

  const [content, setContent] = useState(template);
  const [editorKey, setEditorKey] = useState(performance.now());
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
  const [initialDocumentJson] = useState(form.templateJson);
  const [isDirty, setIsDirty] = useState(false);

  const documentTypeName = useSelector(selectDocumentTypeNameById(documentTypeId));
  const dynamicFieldsSchema = useSelector(selectDynamicFieldsSchema);
  const dynamicFieldsNamesById = useSelector(selectDynamicFieldsNamesById);

  const isPdfFormType = formType === FormType.PDF;
  const prismBlockReason = useMemo(
    () => getPrismEditorBlockReason({ template, isSeededSystemForm }),
    [template, isSeededSystemForm],
  );
  const isPrismFormEditorEnabled = useIsPrismFormEditorEnabled(
    isTemplateSavedInPrism,
    prismBlockReason,
  );

  const prismEditorRef = useRef();
  const baselineJsonRef = useRef(null);
  const isDocumentChangedRef = useRef(false);
  const isOwnViewRedirectAllowedRef = useRef(false);

  const header = useMemo(
    () => `${name} (${documentTypeName}) - ${getStateName(state)}`,
    [documentTypeName, name, state],
  );

  const editorConfig = useMemo(
    () => ({
      dynamicFieldConfig: {
        dynamicFieldsSchema,
        dynamicFieldsNamesById,
      },
    }),
    [dynamicFieldsSchema, dynamicFieldsNamesById],
  );

  const { onSaveContent, onOverrideForm, onViewForm } = useFormTemplateActions(setIsLoading);

  const onExportPdf = usePrismFormExport({ formId, formName: name, prismEditorRef, setIsLoading });

  const isOverrideEnabled = useFeatureToggle(FeatureFlag.EnableFormLocalOverrides);
  const isInternalCompany = useSelector(getIsFromInternalCompany);
  const isOverridable =
    isOverrideEnabled &&
    !isInternalCompany &&
    checkIsInternalCompany({ companyId: form.companyId }) &&
    !form.hasLocalOverride &&
    !getIsFormNonOverridable({ formType, isCustom: form.isCustom });

  const getLivePrismState = useCallback(
    () => ({
      documentJson: prismEditorRef.current?.getDocumentJson() ?? null,
      htmlContent: prismEditorRef.current?.getDocumentHtml() ?? null,
    }),
    [],
  );

  const getLiveFormState = useCallback(() => {
    const { htmlContent: liveContent, documentJson } = isPrismFormEditorEnabled
      ? getLivePrismState()
      : { htmlContent: content, documentJson: null };

    const isUnchanged = getIsFormContentUnchanged({
      isPrismFormEditorEnabled,
      content: liveContent,
      template,
      baselineJson: baselineJsonRef.current,
      documentJson,
    });

    return { liveContent, documentJson, isUnchanged };
  }, [isPrismFormEditorEnabled, getLivePrismState, content, template]);

  const onSaveClick = useCallback(() => {
    const { liveContent, documentJson, isUnchanged } = getLiveFormState();

    if (isUnchanged) {
      onViewForm({ formId, state, formType });

      return;
    }

    // The CKEditor rewrites would put a second style attribute on every table and cell Prism wrote.
    const contentToSend =
      isPdfFormType || isPrismFormEditorEnabled
        ? liveContent
        : replaceFontFamily(setTableStylesToContent(liveContent));

    const saveForm = isOverridable ? onOverrideForm : onSaveContent;

    isOwnViewRedirectAllowedRef.current = true;
    saveForm({
      content: contentToSend,
      formId,
      state,
      formType,
      withPageNumbers,
      document: documentJson,
    }).finally(() => {
      isOwnViewRedirectAllowedRef.current = false;
    });
  }, [
    getLiveFormState,
    isPdfFormType,
    isPrismFormEditorEnabled,
    isOverridable,
    onOverrideForm,
    onSaveContent,
    formId,
    state,
    formType,
    withPageNumbers,
    onViewForm,
  ]);

  const onCancelClick = useCallback(() => {
    const { isUnchanged } = getLiveFormState();

    if (isUnchanged) {
      onViewForm({ formId, state, formType, selectedPage });

      return;
    }

    const params = {
      type: ConfirmationModalType.WARNING,
      message: 'Confirm cancel the editing',
      description:
        'Are you sure you want to cancel the form editing? All unsaved data will be lost.',
      onAccept: () => {
        isOwnViewRedirectAllowedRef.current = true;
        onViewForm({ formId, state, formType, selectedPage });
      },
    };

    dispatch(openConfirmationDialogAction(params));
  }, [getLiveFormState, dispatch, formId, onViewForm, state, formType, selectedPage]);

  const debouncedOnChange = useDebounce(content => {
    setContent(content);
    setIsSaveButtonDisabled(false);
  }, SCROLL_DELAY_MS);

  const debouncedOnChangeWithSaveButtonDisabling = useCallback(
    content => {
      setIsSaveButtonDisabled(true);
      debouncedOnChange(content);
    },
    [debouncedOnChange],
  );

  // The leave guard covers the Prism editor only. Prism also reports onChange when it mounts and
  // when the selection moves, so only its document changes mark the form as edited.
  const onDocumentChange = useCallback(() => {
    isDocumentChangedRef.current = true;
    setIsDirty(true);
    debouncedOnChangeWithSaveButtonDisabling(content);
  }, [content, debouncedOnChangeWithSaveButtonDisabling]);

  // While a save or a confirmed Cancel is under way, only their own push to this form's view is let
  // through; Back or any other way out still asks, since a failed save keeps the edits unsaved.
  const onLeaveAttempt = useCallback(
    ({ pathname }, action) => {
      const isOwnViewRedirect =
        isOwnViewRedirectAllowedRef.current &&
        action === OWN_VIEW_REDIRECT_HISTORY_ACTION &&
        FormsPaths.viewForm.matchPath(pathname)?.params[FORMS_ROUTING_PARAMS.formId] === formId;

      return isOwnViewRedirect || getLiveFormState().isUnchanged || UNSAVED_LEAVE_PROMPT_MESSAGE;
    },
    [getLiveFormState, formId],
  );

  const formActions = useMemo(() => {
    const buttons = [
      {
        disabled: isLoading || isSaveButtonDisabled,
        icon: SaveIcon,
        testName: 'save',
        title: 'Save form content',
        onClick: onSaveClick,
      },
      {
        disabled: isLoading,
        icon: ErrorIcon,
        testName: 'cancel',
        title: 'Cancel editing',
        onClick: onCancelClick,
        disableRipple: true,
      },
    ];

    return (
      <>
        <FormChangeHistoryButton formId={formId} formName={name} isGlobalForm={isGlobal} />
        {buttons.map(({ testName, ...props }) => (
          <ButtonWithTooltip
            key={props.title}
            {...props}
            data-testid={FormsTestIds.getFormBuilderActionButtonId(testName)}
          />
        ))}
      </>
    );
  }, [isLoading, onCancelClick, onSaveClick, isSaveButtonDisabled, formId, name, isGlobal]);

  useBeforeUnloadPrompt(isDirty, UNSAVED_LEAVE_PROMPT_MESSAGE);

  useEffectSafeStrictMode(() => {
    dispatch(
      getDynamicFieldsSchemaAction({ formId, dynamicFieldsScope: DynamicFieldType.FORMS }),
    ).catch(Error);
  }, [dispatch, formId]);

  useEffect(() => {
    // CKEditor reads its config only when created, so it is recreated when the catalog changes.
    // Prism takes the catalog names as a live prop and keeps the document and its edits.
    if (isPrismFormEditorEnabled) {
      return;
    }

    setEditorKey(performance.now());
  }, [editorConfig, isPrismFormEditorEnabled]);

  useEffect(() => {
    if (!isPrismFormEditorEnabled || isDocumentChangedRef.current) {
      return;
    }

    baselineJsonRef.current = prismEditorRef.current?.getDocumentJson() ?? null;
  }, [editorKey, isPrismFormEditorEnabled]);

  return (
    <>
      <Prompt message={onLeaveAttempt} when={isDirty} />
      <FormEditor
        isEditMode
        isLegacyTemplateHtml
        config={editorConfig}
        formActions={formActions}
        header={header}
        initialDocumentJson={initialDocumentJson}
        isTemplateSavedInPrism={isTemplateSavedInPrism}
        key={editorKey}
        prismBlockReason={prismBlockReason}
        prismEditorRef={prismEditorRef}
        value={content}
        onChange={debouncedOnChangeWithSaveButtonDisabling}
        onDocumentChange={onDocumentChange}
        onExportPdf={onExportPdf}
      />
    </>
  );
};

FormBuilder.propTypes = {
  form: PropTypes.object.isRequired,
  ...WithFormContentPropTypes,
  ...WithLoadingPropTypes,
};

export default compose(withFormTemplateContent, withLoading, memo)(FormBuilder);
